import {
  Input,
  Box,
  Text,
  VStack,
  Button,
  Spinner,
  IconButton,
  Grid,
  GridItem,
  HStack,
  Link,
  InputProps,
  Badge,
  BoxProps,
  useToast,
} from "@chakra-ui/react";
import { Select, AsyncSelect } from "chakra-react-select";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CurrencyInput from "react-currency-input-field";
import { Controller, useForm } from "react-hook-form";
import ReactS3Uploader from "react-s3-uploader";
import {
  FaChartBar,
  FaFile,
  FaMapMarkerAlt,
  FaGlobe,
  FaTrash,
  FaUser,
  FaPlus,
  FaMinus,
  FaEdit,
} from "react-icons/fa";
import { useLoading } from "../../../../hooks/form";
import { useDialog, useDialogWithOptions } from "../../../../../Common/Dialog";
import actions from "../../../../../actions";
import dayjs from "dayjs";
import { StepProps } from "./CreateCampaignWizard";
import ColumnMatchingModal, {
  ColumnMatchingModalContext,
  withColumnMatchingModal,
} from "./ColumnMatchingDialog";
import WhiteBgInput from "./WhiteBgInput";
import { yupResolver } from "@hookform/resolvers/yup";
import { AdFormData, AdFormSchema } from "./forms/adset";
import ValidatedField from "./ValidatedField";

const optimizationGoals = {
  conversion: [
    { label: "Maximize number of conversions", value: "OFFSITE_CONVERSIONS" },
    { label: "Maximize order value", value: "VALUE" },
  ],
  awareness: [
    { label: "Maximize Landing Page Views", value: "LANDING_PAGE_VIEWS" },
    { label: "Maximize link clicks", value: "LINK_CLICKS" },
  ],
};

const defaultForGoal = (campaignType: string) => {
  if (campaignType === "conversion") {
    return {
      value: "OFFSITE_CONVERSIONS",
      label: "Maximize number of conversions",
    };
  }

  return {
    value: "LANDING_PAGE_VIEWS",
    label: "Maximize Landing Page Views",
  };
};

const labelForType = (type: string) => {
  switch (type) {
    case "countries":
      return "Countries";
    case "interests":
      return "Interests";
    case "behaviors":
      return "Behaviors";
    case "demographics":
      return "Demographics";
    case "geo_markets":
      return "Location";
    case "adeducationschool":
      return "Education";
    case "adworkemployer":
      return "Employer";
    case "adworkposition":
      return "Position";
    default:
      return "Targeting";
  }
};

const TARGETING_TYPES = ["interests", "geo_markets"];

const TARGETING_SUBTYPES = {
  demographics: [
    "adeducationschool",
    "adeducationmajor",
    "adworkemployer",
    "adworkposition",
  ],
};

const IconWrapper = ({
  item,
  isLoading = false,
  onRemove,
}: {
  item: {
    type: string;
    name: string;
    value?: string;
    key?: string;
  };
  isLoading?: boolean;
  onRemove: (item: {
    type: string;
    name: string;
    value?: string;
    key?: string;
  }) => void;
}) => (
  <HStack gap={2} rounded={"lg"} p={2} backgroundColor={"gray.100"}>
    <HStack gap={1} flexShrink={0}>
      {iconForType(item.type)}
      <Text fontWeight={"bold"} fontSize="sm" textTransform={"uppercase"}>
        {item.name}
      </Text>
    </HStack>
    <IconButton
      isLoading={isLoading}
      isDisabled={isLoading}
      size={"xs"}
      aria-label="Remove"
      icon={<FaTrash />}
      onClick={() => onRemove(item)}
    />
  </HStack>
);

const iconForType = (type: string) => {
  switch (type) {
    case "countries":
      return <FaMapMarkerAlt size={12} color="gray.300" />;
    case "interests":
      return <FaGlobe size={12} color="gray.300" />;
    case "behaviors":
      return <FaUser size={12} color="gray.300" />;
    case "demographics":
      return <FaChartBar size={12} color="gray.300" />;
    case "geo_markets":
      return <FaMapMarkerAlt size={12} color="gray.300" />;
    default:
      return <FaGlobe size={12} color="gray.300" />;
  }
};

const Section = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => (
  <GridItem>
    <VStack w="full" gap={8}>
      <Text w="full" fontWeight={"bold"}>
        {title}
      </Text>
      <VStack w="full" flexWrap="wrap" flexGrow={0} flexShrink={1}>
        {children}
      </VStack>
    </VStack>
  </GridItem>
);

const AdForm = ({
  onCreateAd,
  adset,
  back,
  pixels = [],
  campaign,
  search,
  currency,
  loading: _outsideLoading,
  createUploadUrl,
}: StepProps) => {
  const uploadAudienceRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useLoading({
    image: false,
    audience: false,
    targeting: false,
  });
  const [targetingSearchResults, setTargetingSearchResults] = useState<{
    type: string;
    results: { id: string; name: string; key: string }[];
  }>({
    type: "",
    results: [],
  });
  const toast = useToast();
  const dialog = useDialog();
  const [audiences, setAudiences] = useState<
    {
      name: string;
      id: string;
      delivery_status: {
        code: string;
        description: string;
      };
      approximate_count_upper_bound: number;
    }[]
  >([]);

  const [audienceColumns, setAudienceColumns] = useState<{
    sample: string[][];
    schema_fields: string[];
    file_key: string;
    file_name: string;
    file_description: string;
    saved_schema_fields: string[];
  }>({
    sample: [],
    schema_fields: [],
    file_key: "",
    file_name: "",
    file_description: "",
    saved_schema_fields: [],
  });

  const listAudiences = useCallback(
    ({ ad_account_id, next }: { ad_account_id: string; next: boolean }) => {
      setLoading("audiences", true);

      return actions.ADMIN.listAudiences(campaign.event_id, {
        ad_account_id,
      })
        .then((res) => {
          setAudiences(res.audiences.data);
        })
        .finally(() => {
          setLoading("audiences", false);
        });
    },
    [campaign]
  );

  const renderAudience = useCallback(
    ({ id }: { id: string }) => {
      const audience = audiences.find((a) => a.id === id);

      if (!audience) {
        return null;
      }

      return {
        label: (
          <HStack
            justifyContent={"space-between"}
            flexGrow={0}
            flexShrink={1}
            py={2}
            textOverflow={"ellipsis"}
            overflow={"hidden"}
            flexWrap={"wrap"}
            fontSize="xs"
          >
            {audience.delivery_status?.code ? (
              <Badge colorScheme="red">error</Badge>
            ) : null}
            <VStack
              w="full"
              alignItems={"flex-start"}
              textOverflow={"ellipsis"}
              flexWrap={"wrap"}
            >
              <Text textOverflow={"ellipsis"} overflow={"hidden"} w="full">
                {audience.name}
              </Text>
              {audience.delivery_status?.code ? (
                <Text
                  fontSize={"xs"}
                  color="red.500"
                  overflow={"hidden"}
                  textOverflow={"ellipsis"}
                >
                  {audience.delivery_status?.description}
                </Text>
              ) : null}
            </VStack>

            <Text fontSize={"xs"} color="gray.500">
              (under {audience.approximate_count_upper_bound} people)
            </Text>
          </HStack>
        ),
        value: audience.id,
      };
    },
    [audiences]
  );

  useEffect(() => {
    try {
      listAudiences({ ad_account_id: campaign.ad_account_id, next: false });
    } catch (e) {
      console.log(e);
    }
  }, [campaign]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    getValues,
    setValue,
    reset,
    watch,
    trigger,
  } = useForm<AdFormData>({
    resolver: yupResolver(AdFormSchema),
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",

    values: {
      id: adset?.id || "",
      external_id: adset?.external_id || "",
      name: adset?.name || "",
      campaign_id: campaign?.id,
      custom_audience_files: adset?.custom_audience_files || [],
      lifetime_budget: adset?.lifetime_budget
        ? adset?.lifetime_budget.amount / 100
        : undefined,
      age_min: adset?.targeting.age_min || 18,
      age_max: adset?.targeting.age_max || 65,
      campaign_type: campaign?.campaign_type,
      optimization_goal: adset?.optimization_goal
        ? optimizationGoals[campaign?.campaign_type]?.find(
            (o) => o.value === adset.optimization_goal
          )
        : defaultForGoal(campaign?.campaign_type),
      start_time: adset?.start_time
        ? dayjs(adset?.start_time).format("YYYY-MM-DDTHH:mm")
        : undefined,
      stop_time: adset?.stop_time
        ? dayjs(adset?.stop_time).format("YYYY-MM-DDTHH:mm")
        : undefined,
      targeting_query: "",
      targeting_type: {
        value: "geo_markets",
        label: "Location",
      },
      targeting_subtype: undefined,
      interests: adset?.targeting.interests || [],
      geo_markets:
        adset?.targeting.geo_locations?.geo_markets?.map((r) => ({
          key: r.key,
          name: r.name,
        })) || [],
      countries:
        adset?.targeting?.geo_locations?.countries?.map((c) => ({
          id: c,
          name: c,
        })) || [],

      audience_ids: adset?.targeting.custom_audiences
        ? adset.targeting.custom_audiences.map((id) => ({
            value: id,
            label: `${audiences?.find((a) => a.id === id)?.name || id}`,
          }))
        : [],
      pixel_id: adset?.pixel_id
        ? {
            value: adset.pixel_id,
            label: `${pixels?.find((p) => p.id === adset.pixel_id)?.name} (${
              adset.pixel_id
            })`,
          }
        : {
            value: pixels?.at(0)?.id,
            label: `${pixels?.at(0)?.name} (${pixels?.at(0)?.id})`,
          },
    },
  });

  console.log("audiences", audiences);
  console.log(watch("audience_ids"));

  const isLoading =
    _outsideLoading.adsets ||
    _outsideLoading.campaigns ||
    _outsideLoading.audiences ||
    _outsideLoading.pixels ||
    _outsideLoading.publish ||
    _outsideLoading.pages ||
    _outsideLoading.instagramAccounts;

  const campaignType = watch("campaign_type");
  const audienceFiles = watch("custom_audience_files");

  const optimizationGoalOptions = useMemo(() => {
    return (
      optimizationGoals[campaignType] ||
      optimizationGoals[campaign?.campaign_type] ||
      optimizationGoals.conversion
    );
  }, [campaignType, campaign]);

  const _onSubmit = async (data: any) => {
    return onCreateAd({
      ...data,
      pixel_id: data.pixel_id?.value,
      media: {
        id: data.media?.id,
        video: data.media?.video
          ? {
              fileKey: data.media?.video?.fileKey,
              source: data.media?.video?.source?.startsWith("blob:")
                ? undefined
                : data.media?.video?.source,
              thumbnail_url: data.media?.video?.thumbnail_url,
              thumbnailFileKey: data.media?.video?.thumbnailFileKey,
              description: data.media?.video?.description,
              title: data.media?.video?.title,
              external_id: data.media?.video?.external_id,
            }
          : undefined,
        images: data.media?.images
          ? data.media?.images.map((i: any) => ({
              id: i.id,
              fileKey: i.fileKey,
              // To ignore preview images
              url: i.url.startsWith("blob:") ? undefined : i.url,
              caption: i.caption,
            }))
          : undefined,
      },
      custom_audience_files: data.custom_audience_files,
      optimization_goal: data.optimization_goal?.value,
      page_id: data.page_id?.value,
      instagram_actor_id: data.instagram_actor_id?.value,
      audience_ids: data.audience_ids?.map((a: any) => a.value),
      campaign_id: campaign?.id || data.campaign_id,
    }).then(() => {
      reset();
    });
  };

  const targetingOptions = [
    ...watch("interests").map((i) => ({
      type: "interests",
      value: i.id,
      name: i.name,
    })),
    ...watch("geo_markets").map((g) => ({
      type: "geo_markets",
      value: g.key,
      name: g.name,
    })),
  ];

  const isSelected = (type: string, value: string) => {
    return targetingOptions.some((t: any) => t.value === value);
  };

  const removeTargeting = useCallback(
    (targeting: { type: string; value?: string; key?: string }) => {
      console.log(targeting, getValues(targeting.type as any));

      const options: { value: string; key?: string; id?: string }[] = getValues(
        targeting.type as any
      );
      setValue(
        targeting.type as any,
        options.filter(
          (o) => o.key !== targeting.value && o.id !== targeting.value
        )
      );
    },
    [setValue, getValues]
  );

  const addTargeting = useCallback(
    (targeting: {
      name: string;
      type: string;
      value?: string;
      key?: string;
    }) => {
      const options = watch(targeting.type as any);
      setValue(targeting.type as any, [...options, targeting]);

      console.log("targeting", {
        targeting,
        options,
      });
    },
    [setValue, watch]
  );

  const searchTargetingType = useCallback(
    (query: string, type: keyof typeof search, subtype: string) => {
      setLoading("targeting", true);
      const searchFn = search[type];
      if (!searchFn) {
        dialog({
          text: "No search function for this targeting type",
        });
        setLoading("targeting", false);
        return;
      }

      return searchFn(query).then((data) => {
        setLoading("targeting", false);
        setTargetingSearchResults({
          type,
          results: data.data.map((r) => ({
            ...r,
            name:
              "country_code" in r ? `${r.name} (${r.country_code})` : r.name,
            key: r.id || r.key,
          })),
        });
      });
    },
    [search]
  );

  const { open: openColumnMatchingDialog, close: closeColumnMatchingDialog } =
    useDialogWithOptions(ColumnMatchingModalContext);

  const onSearchAudiences = useCallback(
    (inputValue: string) => {
      return actions.ADMIN.searchAudiences(campaign.event_id, {
        ad_account_id: campaign.ad_account_id,
        query: inputValue,
      }).then((res) => {
        return res.audiences.data.map((a) => ({
          label: a.name,
          value: a.id,
        }));
      });
    },
    [campaign]
  );

  const startCustomAudienceColumnMatching = useCallback(
    async ({
      file_key,
      name,
      description,
      schema_fields: schema_fields_from_file,
    }: {
      file_key: string;
      name: string;
      description: string;
      schema_fields: string[];
    }) => {
      try {
        const { sample, schema_fields } =
          await actions.ADMIN.fetchCustomAudienceFileColumns({
            event_id: campaign.event_id,
            file_key,
          });

        setAudienceColumns({
          sample,
          schema_fields,
          saved_schema_fields: schema_fields_from_file,
          file_key,
          file_name: name,
          file_description: description,
        });

        openColumnMatchingDialog();
      } catch (e) {
        toast({
          title: "File format error",
          description: e.message,
          status: "error",
        });
      }
    },
    [campaign, adset]
  );

  const targetingSubtypeOptions =
    TARGETING_SUBTYPES[watch("targeting_type")?.value];

  const targetingType = watch("targeting_type")?.value;

  const searchTargeting = useCallback(
    ({
      query,
      type,
      subtype,
    }: {
      query: string;
      type: string;
      subtype: string;
    }) => {
      searchTargetingType(query, type as any, subtype);
    },
    [targetingType, watch]
  );

  return (
    <VStack w="full" textAlign={"left"} py={8}>
      <ColumnMatchingModal
        title="Matching columns"
        audienceColumns={audienceColumns}
        onSave={async ({
          file_key,
          schema_fields,
          file_name,
          file_description,
        }) => {
          const index = watch(`custom_audience_files`).findIndex(
            (f) => f.file_key === file_key
          );

          if (index !== -1) {
            setValue(
              `custom_audience_files`,
              watch(`custom_audience_files`).map((f, i) =>
                i === index ? { ...f, schema_fields } : f
              ),
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
          } else {
            setValue(
              `custom_audience_files`,
              [
                ...watch(`custom_audience_files`),
                {
                  file_key: file_key,
                  schema_fields: schema_fields,
                  name: file_name,
                  description: file_description,
                },
              ],
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
          }

          closeColumnMatchingDialog();
        }}
      />
      <Grid
        w="full"
        templateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
        gap={16}
      >
        <Section title="Adset details">
          <Box w="full">
            <Text w="full">Name</Text>
            <WhiteBgInput
              placeholder="Name"
              {...register(`name`, { required: true })}
            />
          </Box>
          <ValidatedField errors={errors} name="optimization_goal">
            <Text w="full">Goal</Text>
            <Controller
              control={control}
              name="optimization_goal"
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  isDisabled={isLoading}
                  value={field.value}
                  onChange={(values) => field.onChange(values)}
                  chakraStyles={{
                    container: () => ({
                      position: "relative",
                      width: "100%",
                      backgroundColor: "white",
                    }),
                  }}
                  // @ts-ignore
                  options={optimizationGoalOptions.map((g) => ({
                    label: g.label,
                    value: g.value,
                  }))}
                />
              )}
            />
          </ValidatedField>
          <ValidatedField errors={errors} name="pixel_id">
            <Text w="full">Pixel</Text>
            <Controller
              control={control}
              name="pixel_id"
              render={({ field }) => (
                <Select
                  isDisabled={isLoading}
                  value={field.value}
                  onChange={(values) => field.onChange(values)}
                  chakraStyles={{
                    container: () => ({
                      position: "relative",
                      width: "100%",
                      backgroundColor: "white",
                    }),
                  }}
                  // @ts-ignore
                  options={pixels.map((g) => ({
                    label: `${g.name} (${g.id})`,
                    value: g.id,
                  }))}
                />
              )}
            />
          </ValidatedField>
        </Section>
        <Section title="Budget">
          <ValidatedField errors={errors} name="lifetime_budget">
            <Text w="full">Budget</Text>
            <Controller
              control={control}
              name="lifetime_budget"
              render={({ field }) => (
                <CurrencyInput
                  //@ts-ignore
                  w="100%"
                  isDisabled={isLoading}
                  placeholder="$"
                  defaultValue={field.value}
                  disabled={isLoading}
                  onValueChange={field.onChange}
                  intlConfig={{
                    locale: "en-US",
                    currency,
                  }}
                  customInput={WhiteBgInput}
                  step={1}
                />
              )}
            />
          </ValidatedField>
          <ValidatedField errors={errors} name="start_time">
            <Text w="full">Start Time</Text>
            <WhiteBgInput
              type="datetime-local"
              isDisabled={isLoading}
              {...register(`start_time`)}
            />
          </ValidatedField>
          <ValidatedField errors={errors} name="stop_time">
            <Text w="full">Stop Time</Text>
            <WhiteBgInput
              type="datetime-local"
              isDisabled={isLoading}
              {...register(`stop_time`)}
            />
          </ValidatedField>
        </Section>

        <Section title="Targeting">
          <ValidatedField errors={errors} name="age_min">
            <Text w="full">Age</Text>
            <HStack w="full">
              <WhiteBgInput
                type="number"
                placeholder="Minimum age"
                isDisabled={isLoading}
                {...register(`age_min`)}
              />
              <WhiteBgInput
                type="number"
                placeholder="Maximum age"
                isDisabled={isLoading}
                {...register(`age_max`)}
              />
            </HStack>
          </ValidatedField>
          <VStack w="full" mt={8}>
            <VStack w="full">
              <HStack w="full">
                <Controller
                  control={control}
                  name="targeting_type"
                  render={({ field }) => (
                    <Select
                      isDisabled={isLoading}
                      value={field.value}
                      defaultValue={field.value}
                      placeholder={`Select targeting type`}
                      //@ts-ignore
                      onChange={(values) => {
                        field.onChange(values);
                        setValue("targeting_query", "");
                        setTargetingSearchResults({
                          type: "",
                          results: [],
                        });
                      }}
                      chakraStyles={{
                        container: () => ({
                          position: "relative",
                          width: "100%",
                          backgroundColor: "white",
                        }),
                      }}
                      //@ts-ignore
                      options={TARGETING_TYPES.map((t) => ({
                        label: labelForType(t),
                        value: t,
                      }))}
                    />
                  )}
                />
                {targetingSubtypeOptions && (
                  <Controller
                    control={control}
                    name="targeting_subtype"
                    render={({ field }) => (
                      <Select
                        isDisabled={isLoading}
                        value={field.value}
                        placeholder={`Subtype`}
                        defaultValue={field.value}
                        onChange={(values) => field.onChange(values)}
                        chakraStyles={{
                          container: () => ({
                            position: "relative",
                            width: "100%",
                            backgroundColor: "white",
                          }),
                        }}
                        // @ts-ignore
                        options={targetingSubtypeOptions.map((t) => ({
                          label: labelForType(t),
                          value: t,
                        }))}
                      />
                    )}
                  />
                )}
                <WhiteBgInput
                  placeholder="Search"
                  {...register(`targeting_query`)}
                />

                <Button
                  flexShrink={0}
                  isLoading={isLoading}
                  onClick={() =>
                    searchTargeting({
                      query: watch("targeting_query"),
                      type: watch("targeting_type")?.value,
                      subtype: watch("targeting_subtype")?.value,
                    })
                  }
                >
                  Search
                </Button>
              </HStack>
              {loading.targeting && (
                <HStack w="full" justifyContent={"center"}>
                  <Spinner />
                </HStack>
              )}

              <VStack w="full" px={4} maxH="500px" overflowY="scroll">
                {targetingSearchResults.results.map((t) => (
                  <HStack w="full" justifyContent="space-between">
                    <Text>{t.name}</Text>
                    <IconButton
                      variant="ghost"
                      isLoading={isLoading}
                      isDisabled={isLoading}
                      onClick={() =>
                        isSelected(targetingSearchResults.type, t.id)
                          ? removeTargeting({
                              ...t,
                              type: targetingSearchResults.type,
                            })
                          : addTargeting({
                              ...t,
                              type: targetingSearchResults.type,
                            })
                      }
                      aria-label="Ad targeting"
                      icon={
                        isSelected(targetingSearchResults.type, t.id) ? (
                          <FaMinus />
                        ) : (
                          <FaPlus />
                        )
                      }
                    />
                  </HStack>
                ))}
              </VStack>
            </VStack>
            <Box w="full">
              <HStack
                w="full"
                gap={1}
                justifyContent={"flex-start"}
                flexWrap={"wrap"}
              >
                {targetingOptions.map((t) => (
                  <IconWrapper
                    item={t}
                    isLoading={isLoading}
                    onRemove={removeTargeting}
                  />
                ))}
              </HStack>
            </Box>
          </VStack>
        </Section>

        <Section title="Audiences">
          <VStack w="full" flexGrow={0} flexShrink={0}>
            <Text w="full">Audiences</Text>
            <Controller
              control={control}
              name="audience_ids"
              render={({ field }) => (
                <AsyncSelect
                  isMulti
                  pageSize={20}
                  isDisabled={isLoading}
                  value={field.value}
                  captureMenuScroll
                  formatOptionLabel={(option) => {
                    return renderAudience({ id: option.value })?.label || "";
                  }}
                  onChange={(values) => {
                    console.log("values", values);
                    field.onChange(values);
                  }}
                  chakraStyles={{
                    container: () => ({
                      flexGrow: 0,
                      flexShrink: 1,
                      // maxWidth: "400px",
                      position: "relative",
                      width: "100%",
                      backgroundColor: "white",
                    }),
                  }}
                  loadOptions={(inputValue) => {
                    return onSearchAudiences(inputValue);
                  }}
                  // @ts-ignore
                  defaultOptions={audiences.map((a, i) => ({
                    label: a.name,
                    value: a.id,
                    key: `${a.id}-${i}`,
                  }))}
                />
              )}
            />
          </VStack>
          <ReactS3Uploader
            style={{ display: "none" }}
            //@ts-ignore
            inputRef={uploadAudienceRef}
            getSignedUrl={(file, callback) => createUploadUrl(file, callback)}
            accept="text/csv"
            preprocess={(f, next) => {
              setLoading("audience", true);
              next(f);
            }}
            onFinish={async (result, file) => {
              setLoading("audience", false);
              startCustomAudienceColumnMatching({
                file_key: result.fileKey,
                name: file.name,
                description: file.name,
                schema_fields: audienceColumns.saved_schema_fields,
              });

              //@ts-ignore
              if (uploadAudienceRef.current) {
                uploadAudienceRef.current.value = "";
              }
            }}
            uploadRequestHeaders={(file) => ({
              "Content-Type": file.type,
              "x-amz-acl": "private",
            })}
            contentDisposition="auto"
          />
          <VStack w="full" py={4}>
            <Text w="full">Custom audiences</Text>
            {audienceFiles.map((file) => (
              <HStack w="full" justifyContent={"space-between"}>
                <HStack
                  backgroundColor={"gray.100"}
                  rounded={"lg"}
                  p={2}
                  flexGrow={1}
                >
                  <FaFile />
                  <Text key={file.file_key}>{file.name}</Text>
                </HStack>
                <Box>
                  <IconButton
                    variant={"ghost"}
                    isDisabled={isLoading || !!file.id || !!file.external_id}
                    onClick={() => {
                      setLoading("audience", true);
                      startCustomAudienceColumnMatching({
                        file_key: file.file_key,
                        name: file.name,
                        description: file.description,
                        schema_fields: file.schema_fields,
                      }).finally(() => {
                        setLoading("audience", false);
                      });
                    }}
                    aria-label="Edit audience file"
                    icon={<FaEdit />}
                  />
                  <IconButton
                    variant={"ghost"}
                    isDisabled={isLoading}
                    onClick={() => {
                      setValue(
                        `custom_audience_files`,
                        audienceFiles.filter((f) => f !== file)
                      );
                    }}
                    aria-label="Remove audience file"
                    icon={<FaTrash />}
                  />
                </Box>
              </HStack>
            ))}
          </VStack>
          <VStack w="full">
            <Text fontSize={"xs"}>
              To be able to upload a customer list, you must have accepted the
              Terms of Service.
            </Text>
            <Link
              href={`https://business.facebook.com/ads/manage/customaudiences/tos/?act=${campaign?.ad_account_id.replace(
                "act_",
                ""
              )}`}
              w="full"
              fontSize={"xs"}
              color="blue.500"
              target="_blank"
            >
              Link to terms of service
            </Link>
          </VStack>
          <Button
            onClick={() => uploadAudienceRef.current?.click()}
            isLoading={loading.audience}
            isDisabled={isLoading}
          >
            Upload customer list
          </Button>
        </Section>
      </Grid>
      <HStack w="full" justifyContent={"flex-end"}>
        <Button onClick={back} isLoading={isLoading} isDisabled={isLoading}>
          Back
        </Button>
        <Button
          isLoading={isLoading}
          isDisabled={isLoading || !isValid || !isDirty}
          onClick={handleSubmit(_onSubmit)}
          colorScheme={adset ? "green" : "gray"}
        >
          {adset ? "Save" : "Create"}
        </Button>
      </HStack>
    </VStack>
  );
};

export default withColumnMatchingModal(AdForm);
